/**
 * Supplier Portal Page - Scaffolded for Internal Preview
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, FileText, Receipt, FileCheck, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

// Mock data for scaffolded view
const mockDocuments = [
  { id: 1, type: 'invoice', name: 'INV-2025-001.pdf', date: '2025-01-25', amount: 12500, status: 'pending' },
  { id: 2, type: 'invoice', name: 'INV-2025-002.pdf', date: '2025-01-20', amount: 8750, status: 'approved' },
  { id: 3, type: 'quote', name: 'QUOTE-2025-015.pdf', date: '2025-01-22', amount: 25000, status: 'pending' },
  { id: 4, type: 'confirmation', name: 'PO-CONF-789.pdf', date: '2025-01-18', amount: 15000, status: 'approved' },
];

export default function SupplierPortalPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending Review</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'invoice': return <Receipt className="w-5 h-5 text-blue-500" />;
      case 'quote': return <FileText className="w-5 h-5 text-purple-500" />;
      case 'confirmation': return <FileCheck className="w-5 h-5 text-green-500" />;
      default: return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-gray-900">Supplier Portal</h1>
          <p className="text-gray-500 text-sm mt-1">
            Upload and manage invoices, quotes, and order confirmations
          </p>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600" size="sm">
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">2</p>
                <p className="text-sm text-gray-500">Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">1</p>
                <p className="text-sm text-gray-500">Quotes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">2</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">2</p>
                <p className="text-sm text-gray-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Area */}
      <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
        <CardContent className="p-8 text-center">
          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="font-semibold text-gray-700 mb-2">Upload Documents</h3>
          <p className="text-gray-500 text-sm mb-4">
            Drag and drop files here, or click to browse
          </p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" size="sm">
              <Receipt className="w-4 h-4 mr-2" />
              Invoice
            </Button>
            <Button variant="outline" size="sm">
              <FileText className="w-4 h-4 mr-2" />
              Quote
            </Button>
            <Button variant="outline" size="sm">
              <FileCheck className="w-4 h-4 mr-2" />
              Order Confirmation
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="invoices">Invoices</TabsTrigger>
              <TabsTrigger value="quotes">Quotes</TabsTrigger>
              <TabsTrigger value="confirmations">Confirmations</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <div className="space-y-3">
                {mockDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      {getTypeIcon(doc.type)}
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-sm text-gray-500">{doc.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-semibold">{doc.amount.toLocaleString()} NOK</span>
                      {getStatusBadge(doc.status)}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Note about scaffolding */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-700">
            <strong>Internal Preview:</strong> Document upload and approval workflow is scaffolded. 
            Full document management with OCR and automatic posting will be available in Phase 2.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
